import { createContext, useContext, useCallback, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(null);

const DEFAULT_DURATION_MS = 4000;
let nextToastId = 0;

/**
 * App-wide toast/snackbar notifications, top-right, auto-dismissing.
 * Introduced because a "saved" confirmation rendered inline at the top of a
 * long form (see branding/pages/GeneralSettingsPage.jsx,
 * LoginBackgroundPage.jsx) is easy to miss without scrolling back up --
 * this is a generically useful replacement for that pattern, not something
 * specific to Branding, so any page can call useToast() rather than each
 * one growing its own scroll-dependent success-banner.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timeoutId = timeoutsRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message, { type = 'success', durationMs = DEFAULT_DURATION_MS } = {}) => {
      const id = ++nextToastId;
      setToasts((prev) => [...prev, { id, message, type }]);
      timeoutsRef.current.set(
        id,
        setTimeout(() => dismissToast(id), durationMs)
      );
    },
    [dismissToast]
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast--${toast.type}`}
            onClick={() => dismissToast(toast.id)}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
